import {
  Switch,
  type SwitchRootChangeEventDetails,
  type SwitchRootProps,
} from '@base-ui/react/switch';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../utils/cn';
import { Privacy } from '../../utils/privacy';

/**
 * Toggle — armory's `ToggleSwitch`
 * (sanctuary-turbo/packages/armory/src/lib/ToggleSwitch) rebuilt on Base UI's
 * Switch part (`Switch.Root` + `Switch.Thumb`) wrapped in a `<label>`.
 *
 * Parity source is the **Forge 2.0** `Toggle` set (node 1502:14), not armory
 * (Armory Port ruling, 2026-09-03: "the armory is not matching the Forge 2.0").
 * Every geometry, colour and shadow value below is a Forge binding:
 *   - track 40×24 pill; thumb 20×20, travelling 2px→18px (translate 0→16px)
 *   - off  = Neutral/400 (`bg-neutral-400`), hover Neutral/500
 *   - on   = the Forge's ultraviolet Surface/Action (`bg-surface-action`) by
 *            default; armory's `color` prop still maps success→green, error→red …
 *   - hover thumb widens to a 24×20 rounded rectangle (r10) and keeps its far edge
 *            pinned: `group-hover:w-6`, plus a -4px `margin-left` on the on side
 *   - thumb carries Forge `Black/Level1_Black` (`shadow-level-1`), Level2 on hover
 *   - disabled = Surface/Disabled track + Surface/Neutral thumb, no shadow
 *
 * Motion is a code decision (the Forge publishes no keyframes). Only the travel
 * springs: the thumb positioner moves its `translate` with `ease-spring` over
 * 300ms. The pill's hover/press stretch is a real `width` change on a short
 * `ease-out` (150ms) with the shadow moving alongside; the track fill uses
 * `ease-standard` 300ms. Everything is silenced under `motion-reduce`.
 *
 * Deltas that are not Forge pixels: a `focus-visible` outline (a11y, replacing
 * MUI's ripple) and native `disabled` semantics.
 */
export const toggleVariants = cva('bg-neutral-400', {
  variants: {
    // The "on" track fill by palette role — armory's `color` prop. secondary /
    // info are the Forge's ultraviolet action surface (the default on-colour);
    // the rest are armory's palette. Hover darkens through the shared parity
    // token, but only while the switch can change: a read-only switch keeps its
    // colours and loses the pointer and the hover (compound entries below).
    color: {
      primary: 'data-checked:bg-surface-inverse',
      secondary: 'data-checked:bg-surface-action',
      info: 'data-checked:bg-surface-action',
      success: 'data-checked:bg-surface-success',
      warning: 'data-checked:bg-surface-warning',
      error: 'data-checked:bg-surface-danger',
    },
    interactive: { true: 'cursor-pointer hover:bg-neutral-500', false: '' },
  },
  compoundVariants: [
    { color: 'primary', interactive: true, class: 'data-checked:hover:bg-surface-inverse-hover' },
    {
      color: ['secondary', 'info'],
      interactive: true,
      class: 'data-checked:hover:bg-surface-action-hover',
    },
    { color: 'success', interactive: true, class: 'data-checked:hover:bg-surface-success-hover' },
    { color: 'warning', interactive: true, class: 'data-checked:hover:bg-surface-warning-hover' },
    { color: 'error', interactive: true, class: 'data-checked:hover:bg-surface-danger-hover' },
  ],
  defaultVariants: { color: 'secondary', interactive: true },
});

type ToggleCva = VariantProps<typeof toggleVariants>;

/** Palette roles the "on" track accepts — armory's `Color` minus `inherit`. */
export type ToggleColor = NonNullable<ToggleCva['color']>;

/** Where the label sits relative to the switch. */
export type ToggleLabelPlacement = 'start' | 'end';

/** Track geometry, transitions, focus ring and disabled cursor — always applied. */
const trackBase =
  'group relative inline-flex h-6 w-10 shrink-0 rounded-full select-none transition-[background-color] duration-300 ease-standard motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface-action data-disabled:pointer-events-none data-disabled:cursor-default';

/**
 * Thumb positioner — the only thing that springs is the travel. It stays a 20×20 box
 * and only its `translate` animates, so hovering never re-triggers the spring.
 * `will-change-transform` keeps it on its own compositor layer for good: Chrome
 * otherwise promotes the element when the spring starts and demotes it when it ends,
 * and each switch re-rasters the thumb with a visible snap. The pill's hover repaint
 * then stays inside this small layer instead of dirtying the page.
 */
const thumbBase =
  'pointer-events-none absolute top-0.5 left-0.5 flex size-5 items-center transition-[translate] duration-300 ease-spring will-change-transform motion-reduce:transition-none data-checked:translate-x-4';

/**
 * Thumb pill — the visible disc. The Forge's hover/press stretch (20 → 24 wide) is a
 * real `width` change under `rounded-full`, so the shape is the Forge's r10 rounded
 * rectangle at every frame — a circle scaled 1.2× is an ellipse, pinched at the ends
 * and visibly squashed next to the design. The stretch is anchored to the near edge:
 * on the off side the width grows rightwards from x=2; on the on side a -4px
 * `margin-left` moves with the width so the far edge stays pinned at x=38. Width,
 * margin and the Level1 → Level2 shadow share one short `ease-out` and are laid out
 * and painted together in the same frame. The shift must not be a `translate`: Chrome
 * runs that on the compositor, a frame out of step with the main-thread width, and
 * promotes the pill to a layer at onset — the pinned edge wobbled and the raster
 * snapped as soon as the hover began. A springing width, or one on the standard
 * curve, read as jitter and lag in earlier reviews.
 */
const pillBase =
  'block h-5 w-5 shrink-0 rounded-full transition-[width,margin-left,box-shadow] duration-150 ease-out motion-reduce:transition-none';

/** Pill skin + hover/press stretch — only when enabled (disabled has no shadow). */
const pillRest = 'bg-neutral-0 shadow-level-1';
// The hover / press stretch, only while the switch can change.
const pillEnabled = cn(
  pillRest,
  'group-hover:w-6 group-hover:shadow-level-2 group-active:w-6 group-active:shadow-level-2 group-data-checked:group-hover:-ml-1 group-data-checked:group-active:-ml-1',
);

/** Shared label-text classes; disabled dims the ink to the Forge disabled ink. */
function labelTextClass(disabled: boolean, privacyLevel: Privacy): string {
  return cn('text-body1', disabled ? 'text-text-disabled' : 'text-text-primary', privacyLevel);
}

export interface ToggleProps extends Omit<
  SwitchRootProps,
  | 'className'
  | 'color'
  | 'style'
  | 'onCheckedChange'
  | 'onBlur'
  | 'onFocus'
  | 'onKeyDown'
  | 'ref'
  | 'children'
  | 'required'
> {
  /** A ref to the switch element (`<span role="switch">`). */
  ref?: React.Ref<HTMLElement>;
  /**
   * The label of the switch. Accepts a node (not just a string) so callers can
   * render inline adornments; its text content supplies the switch's accessible
   * name via the wrapping `<label>`. With no label, name the switch yourself
   * with `aria-label` / `aria-labelledby`.
   */
  label?: React.ReactNode;
  /** Which side of the switch the label sits on. Defaults to `start` (armory fixed it left). */
  labelPlacement?: ToggleLabelPlacement;
  /** A class applied to the switch element. */
  className?: string;
  /** Inline styles for the control (a plain object; Base UI's state-function form is not part of this contract). */
  style?: React.CSSProperties;
  /** A class applied to the wrapping label. */
  labelClassName?: string;
  /**
   * Privacy level applied to the label text in session replay. Defaults to `Privacy.unmask`
   * (armory hard-coded it). Pass `Privacy.mask` / `Privacy.exclude` when the label can carry PII.
   * A recorded addition (review, 2026-09-09): armory's ToggleSwitch had no such prop; the Checkbox does.
   */
  privacyLevel?: Privacy;
  /** The palette role of the switch when on (use the `Color` object). Defaults to the ultraviolet action surface. */
  color?: ToggleColor;
  /** Disables the switch so it cannot be toggled. */
  isDisabled?: boolean;
  /** Marks the switch required for form submission. */
  isRequired?: boolean;
  /**
   * Called when the switch is toggled. Base UI's signature (`checked` plus its
   * event details, whose `.event` is the native change event) rather than
   * armory's bare `ChangeEvent` — see the port report.
   */
  onChange?: (checked: boolean, eventDetails: SwitchRootChangeEventDetails) => void;
  /** Called when the switch loses focus. */
  onBlur?: (event: React.FocusEvent<unknown>) => void;
  /** Called when the switch gains focus. */
  onFocus?: (event: React.FocusEvent<unknown>) => void;
  /** Called on a key-down while the switch is focused. */
  onKeyDown?: (event: React.KeyboardEvent<unknown>) => void;
}

/**
 * A labelled on/off switch. Pass `checked`/`onChange` for a controlled switch,
 * or `defaultChecked` for an uncontrolled one. `color` recolours the on-state;
 * `isDisabled` / `isRequired` for state; `label` + `labelPlacement` for the copy.
 */
export function Toggle(props: ToggleProps) {
  const {
    className,
    color = 'secondary',
    disabled = false,
    isDisabled = false,
    isRequired,
    label,
    labelClassName,
    labelPlacement = 'start',
    onChange,
    privacyLevel = Privacy.unmask,
    readOnly = false,
    ref,
    ...others
  } = props;

  // armory ignored native `disabled` in favour of `isDisabled`; honour both.
  // At the atom tier there is no Base UI Field context, so this matches the
  // switch's own `disabled` state exactly.
  const effectivelyDisabled = disabled || isDisabled;
  // Base UI refuses the change when read-only; the pointer and hover styles go with it.
  const interactive = !effectivelyDisabled && !readOnly;

  const control = (
    <Switch.Root
      ref={ref}
      data-color={color}
      data-label-placement={labelPlacement}
      disabled={effectivelyDisabled}
      readOnly={readOnly}
      required={isRequired}
      onCheckedChange={onChange}
      className={cn(
        trackBase,
        effectivelyDisabled ? 'bg-surface-disabled' : toggleVariants({ color, interactive }),
        className,
      )}
      {...others}
    >
      <Switch.Thumb className={thumbBase}>
        <span
          className={cn(
            pillBase,
            effectivelyDisabled ? 'bg-surface-neutral' : interactive ? pillEnabled : pillRest,
          )}
        />
      </Switch.Thumb>
    </Switch.Root>
  );

  // No label → return the bare switch; the caller supplies the accessible name
  // through `aria-label` / `aria-labelledby`. Wrapping an empty <label> would
  // let Base UI's fallback point aria-labelledby at empty text instead.
  if (label == null) {
    return control;
  }

  const text = <span className={labelTextClass(effectivelyDisabled, privacyLevel)}>{label}</span>;

  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 select-none',
        effectivelyDisabled ? 'cursor-default' : 'cursor-pointer',
        labelClassName,
      )}
    >
      {labelPlacement === 'start' ? text : null}
      {control}
      {labelPlacement === 'end' ? text : null}
    </label>
  );
}
